<script setup lang="ts">
import AppContextRail from '@/components/app-shell/AppContextRail.vue'
import FinestButton from '@/components/FinestButton.vue'
import { ESTATE_OBJECT_IMPORT_ACCEPT, useEstateObjectImport } from '@/composables/useEstateObjectImport'
import { useFeatureFlagStore } from '@/stores/features'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    hasPendingImport: boolean
    separated?: boolean
  }>(),
  {
    separated: false,
  },
)

const { t } = useI18n()

const features = useFeatureFlagStore()

const enabled = computed(() => features.enabled('portfolio_import'))

const { uploading, upload } = useEstateObjectImport()

const processing = computed(() => props.hasPendingImport || uploading.value)

const fileInput = useTemplateRef<HTMLInputElement>('fileInput')

const dropTargetActive = ref(false)

const submit = (file: File | undefined): void => {
  if (processing.value) {
    return
  }

  upload(file)
}

const onSelect = (event: Event): void => {
  const input = event.target as HTMLInputElement

  submit(input.files?.[0])
  input.value = ''
}

const onDragOver = (): void => {
  dropTargetActive.value = !processing.value
}

const onDrop = (event: DragEvent): void => {
  dropTargetActive.value = false
  submit(event.dataTransfer?.files?.[0])
}
</script>

<template>
  <AppContextRail v-if="enabled">
    <button
      type="button"
      class="bg-action hover:bg-action-hover flex size-10 items-center justify-center rounded-[9px] text-white transition-colors disabled:opacity-60"
      :disabled="processing"
      :aria-label="processing ? t('processing') : t('action')"
      :title="processing ? t('processing') : t('action')"
      @click="fileInput?.click()">
      <i :class="processing ? 'pi pi-spinner animate-spin' : 'pi pi-upload'" aria-hidden="true" />
    </button>
  </AppContextRail>

  <div v-if="enabled" :class="separated ? 'border-line border-t pt-6' : ''">
    <div
      class="border-info-subtle-active rounded-[10px] border p-4 transition-colors"
      :class="dropTargetActive ? 'bg-info-subtle-hover' : 'bg-info-subtle'"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="dropTargetActive = false"
      @drop.prevent="onDrop">
      <div class="text-center">
        <span
          class="bg-action mx-auto mb-3 flex size-[36px] items-center justify-center rounded-[9px] text-white"
          aria-hidden="true">
          <i class="pi pi-upload text-lg!" />
        </span>
        <p class="leading-[1.3] font-bold">{{ t('title') }}</p>
      </div>

      <div v-if="processing" class="mt-3 flex flex-col items-center gap-3 text-center" role="status">
        <i class="pi pi-spinner text-action animate-spin text-xl!" aria-hidden="true" />
        <p class="text-ink-2 text-sm leading-[1.5]">{{ t('processing') }}</p>
      </div>

      <template v-else>
        <p class="text-ink-2 mt-1 text-center text-sm leading-[1.5]">{{ t('description') }}</p>

        <FinestButton variant="gradient" class="mt-4 w-full" :label="t('action')" @click="fileInput?.click()" />

        <p class="text-ink-3 mt-2 text-center text-[11px]">{{ t('hint') }}</p>

        <input ref="fileInput" type="file" class="hidden" :accept="ESTATE_OBJECT_IMPORT_ACCEPT" @change="onSelect" />
      </template>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "title": "Bestandsliste importieren",
    "description": "Lade deinen Immobilienbestand als Excel- oder PDF-Datei hoch. Der Assistent liest die Objekte aus und legt sie für dich an.",
    "action": "Bestandsliste auswerten",
    "hint": "oder Datei hierher ziehen · XLSX, XLS, PDF",
    "processing": "Deine Bestandsliste wird verarbeitet. Das kann 10 bis 15 Minuten dauern. Du erhältst nach Abschluss eine E-Mail."
  },
  "en": {
    "title": "Import property list",
    "description": "Upload your property portfolio as an Excel or PDF file. The assistant reads the properties and creates them for you.",
    "action": "Analyse property list",
    "hint": "or drag a file here · XLSX, XLS, PDF",
    "processing": "Your property list is being processed. This can take 10 to 15 minutes. You will receive an email when it is finished."
  }
}
</i18n>
